import React, { useState } from 'react';
import Message from './Message';
import { conversationAnnotationService } from '../../services/api';

const AnnotatableMessage = ({
  message,
  isAnnotated,
  messageAnnotations,
  onAnnotationCreated,
  onAnnotationDeleted
}) => {
  const [isAnnotationMode, setIsAnnotationMode] = useState(false);
  const [annotationTitle, setAnnotationTitle] = useState('');
  const [annotationContent, setAnnotationContent] = useState('');
  const [annotationType, setAnnotationType] = useState('highlight');
  const [color, setColor] = useState('#3B82F6');
  const [tags, setTags] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const annotationTypes = [
    { value: 'highlight', label: 'Highlight', color: '#FEF3C7' },
    { value: 'note', label: 'Note', color: '#DBEAFE' },
    { value: 'flag', label: 'Flag', color: '#FEE2E2' },
    { value: 'category', label: 'Category', color: '#D1FAE5' },
    { value: 'custom', label: 'Custom', color: '#E0E7FF' }
  ];

  const handleTypeChange = (type) => {
    setAnnotationType(type);
    const typeConfig = annotationTypes.find(t => t.value === type);
    if (typeConfig && type !== 'custom') {
      setColor(typeConfig.color);
    }
  };

  const handleCreateAnnotation = async (e) => {
    e.preventDefault();

    if (!annotationTitle.trim()) {
      alert('Please enter an annotation title');
      return;
    }

    setIsCreating(true);
    try {
      const annotationData = {
        conversation_id: message.conversation_id,
        annotation_title: annotationTitle.trim(),
        annotation_content: annotationContent.trim() || null,
        annotation_type: annotationType,
        selected_message_ids: [message.id],
        color: color,
        tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
        scope: 'concierge'
      };

      const result = await conversationAnnotationService.createAnnotation(annotationData);
      console.log('Annotation created:', result);

      // Reset form
      setAnnotationTitle('');
      setAnnotationContent('');
      setAnnotationType('highlight');
      setColor('#3B82F6');
      setTags('');

      // Exit annotation mode
      setIsAnnotationMode(false);

      // Notify parent component
      if (onAnnotationCreated) {
        onAnnotationCreated(result);
      }
    } catch (error) {
      console.error('Error creating annotation:', error);
      alert('Failed to create annotation. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteAnnotation = async (annotationId) => {
    if (window.confirm('Are you sure you want to delete this annotation?')) {
      try {
        await conversationAnnotationService.deleteAnnotation(annotationId);
        if (onAnnotationDeleted) {
          onAnnotationDeleted(annotationId);
        }
      } catch (error) {
        console.error('Error deleting annotation:', error);
        alert('Failed to delete annotation');
      }
    }
  };

  const getAnnotationTypeLabel = (type) => {
    const typeMap = {
      'highlight': 'Highlight',
      'note': 'Note',
      'flag': 'Flag',
      'category': 'Category',
      'custom': 'Custom'
    };
    return typeMap[type] || type;
  };

  return (
    <div className="relative group">
      {/* Message content */}
      <div className="relative">
        <Message message={message} />

        {/* Annotation indicator dots */}
        {isAnnotated && (
          <div className="absolute -top-2 -left-2 flex space-x-1">
            {messageAnnotations.map((annotation, index) => (
              <div
                key={annotation.id}
                className="w-3 h-3 rounded-full border-2 border-white shadow-sm cursor-pointer hover:scale-110 transition-transform duration-200"
                style={{ backgroundColor: annotation.color }}
                title={`${annotation.annotation_title}: ${annotation.annotation_content || 'No content'}`}
                onClick={() => {
                  // Show annotation details on click
                  alert(`${annotation.annotation_title}\n\n${annotation.annotation_content || 'No content'}\n\nType: ${getAnnotationTypeLabel(annotation.annotation_type)}\nTags: ${annotation.tags.join(', ') || 'None'}`);
                }}
              />
            ))}
          </div>
        )}

        {/* Annotation toggle button - appears on hover */}
        <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-all duration-200 transform scale-90 group-hover:scale-100">
          <button
            onClick={() => setIsAnnotationMode(!isAnnotationMode)}
            className={`w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold transition-all duration-200 ${
              isAnnotated
                ? 'bg-purple-500 hover:bg-purple-600 hover:shadow-xl'
                : 'bg-gray-500 hover:bg-gray-600 hover:shadow-xl'
            }`}
            title={isAnnotated ? 'Edit annotations' : 'Add annotation'}
          >
            {isAnnotated ? '✏️' : '+'}
          </button>
        </div>
      </div>

      {/* Inline annotation form */}
      {isAnnotationMode && (
        <div className="mt-3 p-4 bg-gradient-to-r from-gray-50 to-blue-50 border border-blue-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              {isAnnotated ? 'Edit Annotation' : 'Add Annotation'}
            </h4>
            <button
              onClick={() => setIsAnnotationMode(false)}
              className="text-gray-500 hover:text-gray-700 text-sm hover:bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center transition-colors"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleCreateAnnotation} className="space-y-3">
            <div>
              <input
                type="text"
                value={annotationTitle}
                onChange={(e) => setAnnotationTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                placeholder="Annotation title *"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <select
                value={annotationType}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
              >
                {annotationTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-8 h-8 border border-gray-300 rounded cursor-pointer hover:scale-110 transition-transform duration-200"
                />
                <span className="text-xs text-gray-500 font-mono">{color}</span>
              </div>
            </div>

            <div>
              <textarea
                value={annotationContent}
                onChange={(e) => setAnnotationContent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 resize-none"
                placeholder="Additional notes (optional)"
                rows={2}
              />
            </div>

            <div>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                placeholder="Tags separated by commas"
              />
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="submit"
                disabled={isCreating || !annotationTitle.trim()}
                className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 transform hover:scale-105 active:scale-95"
              >
                {isCreating ? (
                  <span className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating...
                  </span>
                ) : (
                  'Save Annotation'
                )}
              </button>
              <button
                type="button"
                onClick={() => setIsAnnotationMode(false)}
                className="px-3 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm font-medium transition-all duration-200 transform hover:scale-105 active:scale-95"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Existing annotations display */}
      {isAnnotated && !isAnnotationMode && (
        <div className="mt-2 space-y-1">
          {messageAnnotations.map((annotation) => (
            <div
              key={annotation.id}
              className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded text-xs shadow-sm hover:shadow-md transition-shadow duration-200"
              style={{ borderLeftColor: annotation.color, borderLeftWidth: '3px' }}
            >
              <div className="flex items-center space-x-2">
                <span
                  className="px-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: annotation.color + '20', color: annotation.color }}
                >
                  {getAnnotationTypeLabel(annotation.annotation_type)}
                </span>
                <span className="font-medium text-gray-800">{annotation.annotation_title}</span>
                {annotation.annotation_content && (
                  <span className="text-gray-600">- {annotation.annotation_content}</span>
                )}
              </div>
              <button
                onClick={() => handleDeleteAnnotation(annotation.id)}
                className="text-red-600 hover:text-red-800 text-xs hover:bg-red-50 rounded-full w-5 h-5 flex items-center justify-center transition-colors"
                title="Delete annotation"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnotatableMessage;
